import { supabase } from '../lib/supabase';

/**
 * User Journey Tracking Service
 * Tracks property views, video plays, appointment bookings, and wishlist adds
 * to measure conversion funnels and identify drop-off points.
 */

// Event types for the conversion funnel
export const JOURNEY_EVENTS = {
  PROPERTY_VIEW: 'property_view',
  PROPERTY_SEARCH: 'property_search',
  VIDEO_PLAY: 'video_play',
  VIDEO_COMPLETE: 'video_complete',
  APPOINTMENT_STARTED: 'appointment_started',
  APPOINTMENT_BOOKED: 'appointment_booked',
  WISHLIST_ADD: 'wishlist_add',
  WISHLIST_REMOVE: 'wishlist_remove',
  INQUIRY_STARTED: 'inquiry_started',
  INQUIRY_SUBMITTED: 'inquiry_submitted',
  ROI_CALCULATED: 'roi_calculated',
};

// Funnel stages in order
export const FUNNEL_STAGES = [
  JOURNEY_EVENTS?.PROPERTY_VIEW,
  JOURNEY_EVENTS?.VIDEO_PLAY,
  JOURNEY_EVENTS?.WISHLIST_ADD,
  JOURNEY_EVENTS?.APPOINTMENT_STARTED,
  JOURNEY_EVENTS?.APPOINTMENT_BOOKED,
];

/**
 * Generate or retrieve a session ID for anonymous tracking
 */
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('kj_session_id');
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
    sessionStorage.setItem('kj_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Track a user journey event
 * @param {string} eventType - One of JOURNEY_EVENTS values
 * @param {object} payload - Event-specific data
 */
export const trackEvent = async (eventType, payload = {}) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    let sessionId = getSessionId();

    const eventData = {
      event_type: eventType,
      session_id: sessionId,
      user_id: user?.id || null,
      property_id: payload?.propertyId || null,
      property_title: payload?.propertyTitle || null,
      property_commune: payload?.propertyCommune || null,
      property_type: payload?.propertyType || null,
      property_price: payload?.propertyPrice ? parseInt(payload?.propertyPrice) : null,
      video_duration: payload?.videoDuration || null,
      video_percent_watched: payload?.videoPercentWatched || null,
      metadata: payload?.metadata ? JSON.stringify(payload?.metadata) : null,
      referrer: document?.referrer || null,
      page_url: window?.location?.href || null,
    };

    const { error } = await supabase?.from('user_journey_events')?.insert(eventData);

    if (error) {
      console.warn('Journey tracking error:', error?.message);
    }
  } catch (err) {
    // Silently fail — tracking must never break the app
    console.warn('Journey tracking failed:', err?.message);
  }
};

/**
 * Track a property view
 */
export const trackPropertyView = (property) => {
  if (!property?.id) return;
  trackEvent(JOURNEY_EVENTS?.PROPERTY_VIEW, {
    propertyId: property?.id,
    propertyTitle: property?.title || property?.custom_headline,
    propertyCommune: property?.commune || property?.commune_name,
    propertyType: property?.property_type || property?.type,
    propertyPrice: property?.price || property?.promotional_price,
  });
};

/**
 * Track a video play event
 */
export const trackVideoPlay = (property, videoData = {}) => {
  trackEvent(JOURNEY_EVENTS?.VIDEO_PLAY, {
    propertyId: property?.id,
    propertyTitle: property?.title || property?.custom_headline,
    propertyCommune: property?.commune || property?.commune_name,
    propertyType: property?.property_type || property?.type,
    propertyPrice: property?.price || property?.promotional_price,
    videoDuration: videoData?.duration,
    metadata: { videoUrl: videoData?.url, videoType: videoData?.type },
  });
};

/**
 * Track video completion (with percent watched)
 */
export const trackVideoComplete = (property, percentWatched = 100) => {
  trackEvent(JOURNEY_EVENTS?.VIDEO_COMPLETE, {
    propertyId: property?.id,
    propertyTitle: property?.title || property?.custom_headline,
    propertyCommune: property?.commune || property?.commune_name,
    videoPercentWatched: percentWatched,
  });
};

/**
 * Track appointment booking started
 */
export const trackAppointmentStarted = (property) => {
  trackEvent(JOURNEY_EVENTS?.APPOINTMENT_STARTED, {
    propertyId: property?.id,
    propertyTitle: property?.title || property?.custom_headline,
    propertyCommune: property?.commune || property?.commune_name,
    propertyType: property?.property_type || property?.type,
    propertyPrice: property?.price || property?.promotional_price,
  });
};

/**
 * Track appointment successfully booked
 */
export const trackAppointmentBooked = (property, appointmentData = {}) => {
  trackEvent(JOURNEY_EVENTS?.APPOINTMENT_BOOKED, {
    propertyId: property?.id,
    propertyTitle: property?.title || property?.custom_headline,
    propertyCommune: property?.commune || property?.commune_name,
    propertyType: property?.property_type || property?.type,
    propertyPrice: property?.price || property?.promotional_price,
    metadata: {
      appointmentType: appointmentData?.type,
      scheduledDate: appointmentData?.scheduledDate,
    },
  });
};

/**
 * Track wishlist add
 */
export const trackWishlistAdd = (property) => {
  trackEvent(JOURNEY_EVENTS?.WISHLIST_ADD, {
    propertyId: property?.id,
    propertyTitle: property?.title || property?.custom_headline,
    propertyCommune: property?.commune || property?.commune_name,
    propertyType: property?.property_type || property?.type,
    propertyPrice: property?.price || property?.promotional_price,
  });
};

/**
 * Track wishlist remove
 */
export const trackWishlistRemove = (property) => {
  trackEvent(JOURNEY_EVENTS?.WISHLIST_REMOVE, {
    propertyId: property?.id,
    propertyTitle: property?.title || property?.custom_headline,
    propertyCommune: property?.commune || property?.commune_name,
  });
};

/**
 * Track a property search event
 */
export const trackPropertySearch = (searchParams = {}) => {
  trackEvent(JOURNEY_EVENTS?.PROPERTY_SEARCH, {
    metadata: {
      query: searchParams?.query || '',
      commune: searchParams?.commune || '',
      propertyType: searchParams?.propertyType || '',
      minPrice: searchParams?.minPrice || null,
      maxPrice: searchParams?.maxPrice || null,
      resultsCount: searchParams?.resultsCount ?? -1,
    },
  });
};

/**
 * Track an ROI calculation
 */
export const trackROICalculation = (calcData = {}) => {
  trackEvent(JOURNEY_EVENTS?.ROI_CALCULATED, {
    propertyId: calcData?.propertyId || null,
    propertyPrice: calcData?.propertyPrice || null,
    metadata: {
      totalROI: calcData?.totalROI,
      capRate: calcData?.capRate,
      cashOnCashReturn: calcData?.cashOnCashReturn,
    },
  });
};

/**
 * Track an inquiry submission
 */
export const trackInquirySubmitted = (property, inquiryData = {}) => {
  trackEvent(JOURNEY_EVENTS?.INQUIRY_SUBMITTED, {
    propertyId: property?.id,
    propertyTitle: property?.title || property?.custom_headline,
    propertyCommune: property?.commune || property?.commune_name,
    propertyType: property?.property_type || property?.type,
    propertyPrice: property?.price || property?.promotional_price,
    metadata: {
      preferredContactTime: inquiryData?.preferredContactTime,
    },
  });
};

/**
 * Fetch funnel analytics data (admin use)
 * Returns counts per event type for a given date range
 */
export const getFunnelAnalytics = async (days = 30) => {
  try {
    const since = new Date();
    since?.setDate(since?.getDate() - days);

    const { data, error } = await supabase?.from('user_journey_events')?.select('event_type, property_id, session_id, created_at')?.gte('created_at', since?.toISOString())?.order('created_at', { ascending: false });

    if (error) return { data: null, error: error?.message };

    // Aggregate counts per event type
    const counts = {};
    const uniqueSessions = new Set();
    const propertyViewMap = {};

    (data || [])?.forEach(event => {
      counts[event.event_type] = (counts?.[event?.event_type] || 0) + 1;
      uniqueSessions?.add(event?.session_id);
      if (event?.event_type === JOURNEY_EVENTS?.PROPERTY_VIEW && event?.property_id) {
        propertyViewMap[event.property_id] = (propertyViewMap?.[event?.property_id] || 0) + 1;
      }
    });

    // Build funnel with conversion rates
    const funnelData = FUNNEL_STAGES?.map((stage, index) => {
      const count = counts?.[stage] || 0;
      const prevCount = index > 0 ? (counts?.[FUNNEL_STAGES?.[index - 1]] || 0) : count;
      const conversionRate = prevCount > 0 ? Math.round((count / prevCount) * 100) : 0;
      return { stage, count, conversionRate };
    });

    // Top viewed properties
    const topProperties = Object.entries(propertyViewMap)?.sort(([, a], [, b]) => b - a)?.slice(0, 5)?.map(([propertyId, views]) => ({ propertyId, views }));

    return {
      data: {
        totalSessions: uniqueSessions?.size,
        eventCounts: counts,
        funnelData,
        topProperties,
        rawEvents: data,
      },
      error: null,
    };
  } catch (err) {
    return { data: null, error: err?.message };
  }
};

export default {
  trackEvent,
  trackPropertyView,
  trackPropertySearch,
  trackVideoPlay,
  trackVideoComplete,
  trackAppointmentStarted,
  trackAppointmentBooked,
  trackWishlistAdd,
  trackWishlistRemove,
  trackROICalculation,
  trackInquirySubmitted,
  getFunnelAnalytics,
  JOURNEY_EVENTS,
  FUNNEL_STAGES,
};
